import React, { useState, useEffect, useRef } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Spinner,
  Progress,
  Alert,
} from "reactstrap"
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from "recharts"
import dayjs from "dayjs"

import { getScoring, calcularScoring } from "api/agro"
import MetodologiaScoring from "./MetodologiaScoring"
import toastHandler from "toastHandler"
import useAuthorization from "hooks/useAuthorization"
import { infoSeveridad } from "helpers/alerta_severidad_helpers"

// Colores por categoría de riesgo
// BDSJEA-194: el color y la etiqueta de la severidad salen del helper unico.

const CATEGORIA_COLORS = {
  A: "#28a745", // Verde - Muy bajo riesgo
  B: "#5cb85c", // Verde claro - Bajo riesgo
  C: "#ffc107", // Amarillo - Riesgo moderado
  D: "#fd7e14", // Naranja - Riesgo alto
  E: "#dc3545", // Rojo - Riesgo muy alto
}

const CATEGORIA_LABELS = {
  A: "Muy Bajo Riesgo",
  B: "Bajo Riesgo",
  C: "Riesgo Moderado",
  D: "Riesgo Alto",
  E: "Riesgo Muy Alto",
}

const ScoringAgro = ({ idProductor }) => {
  const [loading, setLoading] = useState(true)
  const [scoring, setScoring] = useState(null)
  const [calculando, setCalculando] = useState(false)
  // BDSJEA-421: "ok" | "noDisponible" (fallo la carga, distinto de "sin scoring").
  const [estadoCarga, setEstadoCarga] = useState("ok")
  const [avisoCalculo, setAvisoCalculo] = useState(null)
  // Foco: al reintentar el boton desaparece; el foco pasa al bloque de estado.
  const estadoRef = useRef(null)
  const pedirFoco = useRef(false)

  const setSuccess = toastHandler(state => state.setSuccess)

  const { puedeModificarObjeto } = useAuthorization()

  useEffect(() => {
    loadScoring()
  }, [idProductor])

  // BDSJEA-421: el error del scoring no se muestra al usuario en la ficha del
  // productor. Un 404 es el estado normal "sin scoring calculado" (no se loguea);
  // cualquier otra falla de carga deja la seccion en "no disponible" con
  // "Reintentar", sin toast, y queda en consola (warn) con el correlationId.
  // El status se lee de error.response (el interceptor de BDSJEA-312 reescribe
  // error.message, asi que ya no sirve buscar "404" en el texto).
  useEffect(() => {
    if (!loading && pedirFoco.current) {
      pedirFoco.current = false
      estadoRef.current?.focus()
    }
  }, [loading])

  const reintentar = () => {
    pedirFoco.current = true
    loadScoring()
  }

  const loadScoring = async () => {
    setLoading(true)
    setEstadoCarga("ok")
    try {
      const data = await getScoring(idProductor)
      setScoring(data)
    } catch (error) {
      setScoring(null)
      const status = error?.response?.status
      if (status === 404) return
      // 403: falta permiso (el interceptor marca accesoDenegado); no se
      // reintenta. 401: el interceptor ya redirige a login.
      if (error?.accesoDenegado || status === 403) {
        setEstadoCarga("sinPermiso")
        return
      }
      setEstadoCarga("noDisponible")
      console.warn(
        "[scoring] no disponible para el productor",
        idProductor,
        error?.response?.status ?? "",
        error?.correlationId ?? "",
      )
    } finally {
      setLoading(false)
    }
  }

  const handleCalcular = async () => {
    setCalculando(true)
    setAvisoCalculo(null)
    try {
      const nuevoScoring = await calcularScoring(idProductor)
      setScoring(nuevoScoring)
      setEstadoCarga("ok")
      setSuccess("Scoring calculado correctamente")
    } catch (error) {
      // Aviso inline neutro: sin texto crudo del servidor ni toast de error.
      const status = error?.response?.status
      let texto =
        "No se pudo calcular el scoring en este momento. Intente nuevamente más tarde."
      if (error?.accesoDenegado || status === 403) {
        texto = "No tiene permiso para calcular el scoring."
      } else if (status === 400 || status === 422) {
        texto =
          "No se pudo calcular el scoring con los datos actuales del productor."
      }
      setAvisoCalculo(texto)
      console.warn(
        "[scoring] fallo el calculo para el productor",
        idProductor,
        error?.response?.status ?? "",
        error?.correlationId ?? "",
      )
    } finally {
      setCalculando(false)
    }
  }

  const avisoCalculoInline = avisoCalculo && (
    <div role="status" className="alert alert-secondary py-2 mb-3">
      {avisoCalculo}
    </div>
  )

  // Datos para el radar chart - CORREGIDO según ScoringDTO real
  const radarData = scoring
    ? [
        {
          dimension: "Escala Productiva",
          valor: parseFloat(scoring.scoreEscalaProductiva) || 0,
        },
        {
          dimension: "Historial Productivo",
          valor: parseFloat(scoring.scoreHistorialProductivo) || 0,
        },
        {
          dimension: "Condición Actual",
          valor: parseFloat(scoring.scoreCondicionActual) || 0,
        },
        {
          dimension: "Riesgo Climático",
          valor: parseFloat(scoring.scoreRiesgoClimatico) || 0,
        },
        {
          dimension: "Cobertura Riesgos",
          valor: parseFloat(scoring.scoreCoberturaRiesgos) || 0,
        },
        {
          dimension: "Tenencia Tierra",
          valor: parseFloat(scoring.scoreTenenciaTierra) || 0,
        },
        {
          dimension: "Márgenes Rentabilidad",
          valor: parseFloat(scoring.scoreMargeneRentabilidad) || 0,
        },
      ]
    : []

  if (loading) {
    return (
      <div
        className="text-center py-5"
        role="status"
        aria-live="polite"
        tabIndex={-1}
        ref={estadoRef}
      >
        <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
        <p className="mt-3 text-muted">Cargando scoring...</p>
      </div>
    )
  }

  if (!scoring && estadoCarga === "sinPermiso") {
    return (
      <Card>
        <CardBody
          className="text-center py-5"
          role="status"
          aria-live="polite"
          tabIndex={-1}
          innerRef={estadoRef}
        >
          <i
            className="mdi mdi-lock-outline text-muted"
            style={{ fontSize: 64 }}
          />
          <h5 className="mt-3">No tiene permiso para ver el scoring</h5>
        </CardBody>
      </Card>
    )
  }

  if (!scoring && estadoCarga === "noDisponible") {
    return (
      <Card>
        <CardBody
          className="text-center py-5"
          role="status"
          aria-live="polite"
          tabIndex={-1}
          innerRef={estadoRef}
        >
          <i
            className="mdi mdi-chart-arc text-muted"
            style={{ fontSize: 64 }}
          />
          <h5 className="mt-3">Scoring no disponible</h5>
          <p className="text-muted">
            No se pudo obtener el scoring en este momento.
          </p>
          <Button color="primary" outline onClick={reintentar}>
            <i className="mdi mdi-refresh me-1" />
            Reintentar
          </Button>
        </CardBody>
      </Card>
    )
  }

  if (!scoring) {
    return (
      <Card>
        <CardBody
          className="text-center py-5"
          tabIndex={-1}
          innerRef={estadoRef}
        >
          <i
            className="mdi mdi-chart-arc text-muted"
            style={{ fontSize: 64 }}
          />
          <h5 className="mt-3">Sin Scoring Calculado</h5>
          <p className="text-muted">
            Aún no se ha calculado el scoring agropecuario para este productor.
          </p>
          {avisoCalculoInline}
          {puedeModificarObjeto("/agro/establecimientos") && (
            <Button
              color="primary"
              onClick={handleCalcular}
              disabled={calculando}
            >
              {calculando ? (
                <Spinner size="sm" className="me-1" />
              ) : (
                <i className="mdi mdi-calculator me-1" />
              )}
              Calcular Scoring
            </Button>
          )}
        </CardBody>
      </Card>
    )
  }

  const categoriaColor = CATEGORIA_COLORS[scoring.categoriaRiesgo] || "#6c757d"

  return (
    <div tabIndex={-1} ref={estadoRef} style={{ outline: "none" }}>
      {/* Panel de metodologia, mismo patron que Proyeccion Crediticia y el ITA. */}
      <MetodologiaScoring />
      {avisoCalculoInline}

      <Row>
        {/* Score Total */}
        <Col lg={4}>
          <Card
            className="text-center"
            style={{ borderTop: `4px solid ${categoriaColor}` }}
          >
            <CardBody>
              <h6 className="text-muted mb-3">SCORE TOTAL</h6>
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                style={{
                  width: 120,
                  height: 120,
                  backgroundColor: `${categoriaColor}20`,
                  border: `3px solid ${categoriaColor}`,
                }}
              >
                <span
                  style={{
                    fontSize: 36,
                    fontWeight: "bold",
                    color: categoriaColor,
                  }}
                >
                  {parseFloat(scoring.scoreTotal).toFixed(0)}
                </span>
              </div>

              <div>
                <Badge
                  style={{ backgroundColor: categoriaColor, fontSize: 14 }}
                  className="px-3 py-2"
                >
                  Categoría {scoring.categoriaRiesgo}
                </Badge>
                <p className="mb-0 mt-2 text-muted">
                  {CATEGORIA_LABELS[scoring.categoriaRiesgo]}
                </p>
              </div>

              <hr />

              <small className="text-muted">
                Calculado:{" "}
                {dayjs(scoring.fechaCalculo).format("DD/MM/YYYY HH:mm")}
              </small>

              {puedeModificarObjeto("/agro/establecimientos") && (
                <div className="mt-3">
                  <Button
                    color="primary"
                    size="sm"
                    outline
                    onClick={handleCalcular}
                    disabled={calculando}
                  >
                    {calculando ? <Spinner size="sm" /> : "Recalcular"}
                  </Button>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Datos calculados */}
          <Card className="mt-3">
            <CardBody>
              <h6 className="card-title">Datos del Productor</h6>
              <table className="table table-sm table-borderless mb-0">
                <tbody>
                  <tr>
                    <td className="text-muted">Superficie Total</td>
                    <td className="text-end">
                      {scoring.superficieTotalHa?.toLocaleString("es-AR")} ha
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted">Margen Bruto Prom.</td>
                    <td className="text-end">
                      {scoring.margenBrutoPromedioHa
                        ? `USD ${scoring.margenBrutoPromedioHa.toLocaleString("es-AR")}/ha`
                        : "-"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </CardBody>
          </Card>
        </Col>

        {/* Radar Chart */}
        <Col lg={8}>
          <Card>
            <CardBody>
              <h6 className="card-title">Análisis por Dimensión</h6>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 11 }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Radar
                    name="Score"
                    dataKey="valor"
                    stroke={categoriaColor}
                    fill={categoriaColor}
                    fillOpacity={0.4}
                  />
                  <Tooltip
                    formatter={value => [`${value.toFixed(0)} pts`, "Score"]}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>

          {/* Detalle de Scores */}
          <Card className="mt-3 text-muted">
            <CardBody>
              <h6 className="card-title">Detalle de Scores</h6>
              <Row>
                {radarData.map((item, idx) => (
                  <Col md={6} key={idx} className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <small>{item.dimension}</small>
                      <small className="fw-bold">
                        {item.valor.toFixed(0)}/100
                      </small>
                    </div>
                    <Progress
                      value={item.valor}
                      color={
                        item.valor >= 70
                          ? "success"
                          : item.valor >= 40
                            ? "warning"
                            : "danger"
                      }
                      style={{ height: 8 }}
                    />
                  </Col>
                ))}
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Alertas - CORREGIDO según ScoringAlertaDTO */}
      {scoring.alertas && scoring.alertas.length > 0 && (
        <Card className="mt-3 text-muted">
          <CardBody>
            <h6 className="card-title">
              <i className="mdi mdi-alert me-1 text-warning" />
              Alertas ({scoring.alertas.length})
            </h6>
            {scoring.alertas.map((alerta, idx) => {
              const sev = infoSeveridad(alerta.severidad)
              return (
                <Alert key={idx} color={sev.color} className="mb-2">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <strong>{alerta.tipoAlerta}</strong>
                      <span className="ms-2 badge bg-secondary">
                        {alerta.dimensionAfectada}
                      </span>
                      <p className="mb-1 mt-1">{alerta.descripcion}</p>
                      {alerta.recomendacion && (
                        <small className="text-muted">
                          <i className="mdi mdi-lightbulb me-1" />
                          {alerta.recomendacion}
                        </small>
                      )}
                    </div>
                    <Badge color={sev.color}>{sev.etiqueta}</Badge>
                  </div>
                </Alert>
              )
            })}
          </CardBody>
        </Card>
      )}

      {/* Recomendación General */}
      {scoring.recomendacion && (
        <Card className="text-muted mt-3 border-primary">
          <CardBody>
            <h6 className="card-title text-primary">
              <i className="mdi mdi-information me-1" />
              Recomendación
            </h6>
            <p className="mb-0">{scoring.recomendacion}</p>
          </CardBody>
        </Card>
      )}
    </div>
  )
}

export default ScoringAgro
